import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { cloneDeep } from 'es-toolkit';
import { format, isValid } from 'date-fns';

import { Box, FormField, FormHelp, Paragraph, Toast, useToast } from '@zillow/constellation-10';
import { debounce } from '@zg-rentals/zdash';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  DatarouterError,
  useRentalManagerUserPropertySpecialOfferWebApiHandler_create,
  useRentalManagerUserPropertySpecialOfferWebApiHandler_update,
} from '@zg-rentals/rental-manager-env';
import { logError } from '@zg-rentals/log-error';

import { ModularListingContext } from '../../containers/CreateListingPage/context';

import { useSelector } from '../../selectors/useSelector';
import actions from '../../containers/CreateListingPage/actions';
import PropertiesActions from '../../actions/PropertiesActions';

import DatePickerWithDateSelect from '../DatePickerWithDateSelect';
import WizardAlert from '../WizardAlert';

import TimeConstants from '../../constants/TimeConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../constants/WizardConstants';
import WizardServerError from '../../model/WizardServerError';
import SpecialOffersCSEvents from '../../model/ClickstreamEvent/SpecialOffersCSEvents';

import { TRIGGER_LOCATIONS } from '../../constants/ClickstreamConstants';
import { GET_LISTING_RECS } from '../../constants/RecommendationsConstants';
import { SPECIAL_OFFERS_ACTIONS } from '../../constants/SpecialOffersConstants';

import { isValidDateStringFormatMMDDYYYY } from '../../utils/dateUtils';
import clickstreamUtils from '../../utils/clickstreamUtils';

import SpecialOffer from '../../model/SpecialOffer';

import RMWInput from '../RMWInput';

import type { ChangeEvent } from 'react';
import type { SpecialOfferProps } from '../../model/SpecialOffer';

const handleDebounce = (func: (e: ChangeEvent<HTMLInputElement>) => void, wait: number) => debounce(func, wait);

type ErrorProps = { name: string; message: string; errorCode: number };

type Props = {
  action?: string;
  defaultOffer?: SpecialOfferProps;
  onClose?: null | (() => void);
  isMultifamilyForm?: boolean;
};

const SpecialOfferFormField = ({
  action = SPECIAL_OFFERS_ACTIONS.ADD,
  defaultOffer = {
    description: '',
    startDateYyyyMmDd: '',
    endDateYyyyMmDd: '',
    offerIdEncoded: '',
  },
  onClose = null,
  isMultifamilyForm = false,
}: Props) => {
  const dispatch = useDispatch();

  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();

  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj, isSavingSpecialOffers },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const {
    properties: {
      currentProperty,
      currentProperty: { active, dateLastActivatedTimestamp, listingAlias, specialOffers },
    },
    user,
  } = useSelector((state) => state);

  let formAction = action;
  let formOffer = defaultOffer;
  if (!isMultifamilyForm && specialOffers.length > 0) {
    formAction = SPECIAL_OFFERS_ACTIONS.EDIT;
    formOffer = specialOffers[0];
  }
  // represent the start/end date before formatting for BE
  const [specialOffer, setSpecialOffer] = useState(SpecialOffer(formOffer));
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [description, setDescription] = useState(specialOffer.description);
  const [errorObj, setErrorObj] = useState<{ [key: string]: string }>({});

  const { startDateYyyyMmDd, endDateYyyyMmDd, description: initialDescription, offerIdEncoded } = specialOffer;

  useEffect(() => {
    // track first time user clicks to add a special offer on single family listings
    if (specialOffers.length === 0 && !isMultifamilyForm) {
      const triggerLocationSuffix = clickstreamUtils.getListingStatusSuffix(active, dateLastActivatedTimestamp);
      SpecialOffersCSEvents.SpecialOffersAddNew({
        currentProperty,
        user,
        triggerLocation: `${TRIGGER_LOCATIONS.POSTING_PATH_REVIEW}${triggerLocationSuffix}`,
      });
    }
  }, []);

  useEffect(() => {
    if (description !== '') {
      actions.updateIsSaveDisabled({ reducerDispatch, isSaveDisabled: false });
    } else {
      actions.updateIsSaveDisabled({ reducerDispatch, isSaveDisabled: true });
    }

    return () => {
      actions.updateIsSaveDisabled({ reducerDispatch, isSaveDisabled: false });
    };
  }, [description]);

  useEffect(() => {
    if (isSaveComplete || isSavingSpecialOffers) {
      save();
    }
  }, [isSaveComplete, isSavingSpecialOffers]);

  const handleSuccess = (response: SpecialOfferProps) => {
    const specialOffersCopy = cloneDeep(specialOffers);

    if (formAction === SPECIAL_OFFERS_ACTIONS.EDIT) {
      const indexToUpdate = specialOffersCopy.findIndex(
        (offer: SpecialOfferProps) => offer.offerIdEncoded === offerIdEncoded,
      );

      if (indexToUpdate === -1) {
        return setErrorObj({
          ...errorObj,
          save: 'There was an error saving your offer. Please try again.',
        });
      }

      specialOffersCopy[indexToUpdate] = SpecialOffer(response);
    } else {
      specialOffersCopy.push(SpecialOffer(response));
    }

    dispatch(PropertiesActions.updateCurrentProperty({ specialOffers: specialOffersCopy }));
    if (isSaveComplete) {
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
    }

    if (isSavingSpecialOffers) {
      actions.updateAnyProperty({
        reducerDispatch,
        data: {
          isSavingSpecialOffers: false,
        },
      });
    }

    queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
    if (onClose) {
      onClose();
    }
  };

  const handleError = ({ error, contextName }: { error: ErrorProps; contextName: string }) => {
    const message = `Special Offer Error @ ${error.name} where code is ${error.errorCode}`;
    logError({
      error,
      context: {
        contextName,
        message,
      },
    });

    if (isInModalState) {
      updateServerError(error.message, error.name);
    } else {
      enqueueToast(
        <Toast tone="critical">{`There was an error ${formAction === SPECIAL_OFFERS_ACTIONS.ADD ? 'creating' : 'updating'} the offer.`}</Toast>,
      );
    }

    errorObj[error.name] = error.message;

    if (isSavingSpecialOffers) {
      actions.updateAnyProperty({
        reducerDispatch,
        data: {
          isSavingSpecialOffers: false,
        },
      });
    }
  };

  const { mutate: createSpecialOffer } = useRentalManagerUserPropertySpecialOfferWebApiHandler_create({
    onSuccess: handleSuccess,
    onError: (err) => {
      const { errors } = DatarouterError.getErrorDetails(err);

      errors.forEach((error) => {
        handleError({ error, contextName: `PropertiesActions.createSpecialOffer: listingAlias: ${listingAlias}` });
      });
    },
  });

  const { mutate: updateSpecialOffer } = useRentalManagerUserPropertySpecialOfferWebApiHandler_update({
    onSuccess: handleSuccess,
    onError: (err) => {
      const { errors } = DatarouterError.getErrorDetails(err);

      errors.forEach((error) => {
        handleError({ error, contextName: 'PropertiesActions.updateSpecialOffer' });
      });
    },
  });

  const updateServerError = (message = '', fieldName = 'startDate') => {
    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt: DISPLAY_SERVER_ERROR_AT.FIELD,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setErrorObj({});
    clearServerError();
    setDescription(e.target.value);
    setSpecialOffer({ ...specialOffer, description: e.target.value });
  };

  const handleChangeDebounced = handleDebounce(handleChange, TimeConstants.SELECTION_DEBOUNCE_TIME_MS);

  const save = () => {
    if (startDate && !isValidDateStringFormatMMDDYYYY(startDate)) {
      setErrorObj({
        ...errorObj,
        startDate: 'Start date must be in MM/DD/YYYY format.',
      });
      return;
    }

    if (endDate && !isValidDateStringFormatMMDDYYYY(endDate)) {
      setErrorObj({
        ...errorObj,
        endDate: 'End date must be in MM/DD/YYYY format.',
      });
      return;
    }

    const specialOfferCopy = cloneDeep(specialOffer);
    if (isValid(new Date(startDate))) {
      specialOfferCopy.startDateYyyyMmDd = format(new Date(startDate), 'yyyy-MM-dd');
    } else {
      specialOfferCopy.startDateYyyyMmDd = '';
    }

    if (isValid(new Date(endDate))) {
      specialOfferCopy.endDateYyyyMmDd = format(new Date(endDate), 'yyyy-MM-dd');
    } else {
      specialOfferCopy.endDateYyyyMmDd = '';
    }

    setSpecialOffer({
      ...specialOfferCopy,
    });

    const postData = {
      ...specialOfferCopy,
      listingAlias,
    };

    if (formAction === SPECIAL_OFFERS_ACTIONS.EDIT) {
      updateSpecialOffer({ postData });
    } else {
      createSpecialOffer({ postData });
    }
  };

  return (
    <div
      data-testid="SpecialOfferFormField"
      data-eventid={
        specialOffers.length === 0 && !isMultifamilyForm
          ? SpecialOffersCSEvents.SpecialOffersAddNew.data_event_id
          : undefined
      }
    >
      {isInModalState && <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />}

      <Paragraph css={{ marginBottom: '400' }}>
        Share details of the deal or discount you&#39;d like to display to renters. This will appear on your listing
        until your chosen end date.
      </Paragraph>

      <FormField
        error={!!errorObj.startDate}
        description={<FormHelp error={!!errorObj.startDate}>{errorObj.startDate}</FormHelp>}
      >
        <DatePickerWithDateSelect
          label="Offer start date"
          onDateChange={(date) => {
            setErrorObj({});
            setStartDate(date);
            clearServerError();
          }}
          showNextMonthButton={false}
          initialDate={startDateYyyyMmDd}
          setDefaultDate={false}
          defaultPastDateToToday={false}
          saveOnDateSelect={false}
        />
      </FormField>

      <Box css={{ marginBlock: '200' }}>
        <FormField
          error={!!errorObj.endDate}
          description={<FormHelp error={!!errorObj.endDate}>{errorObj.endDate}</FormHelp>}
        >
          <DatePickerWithDateSelect
            label="Offer end date"
            onDateChange={(date) => {
              setErrorObj({});
              setEndDate(date);
              clearServerError();
            }}
            showNextMonthButton={false}
            initialDate={endDateYyyyMmDd}
            setDefaultDate={false}
            minDate={startDateYyyyMmDd || undefined}
            defaultPastDateToToday={false}
            saveOnDateSelect={false}
          />
        </FormField>
      </Box>

      <RMWInput
        data-testid="description-textarea"
        error={!!errorObj.description}
        isRequired={true}
        // @ts-expect-error
        description={
          errorObj.description ? (
            <FormHelp error={true}>{errorObj.description}</FormHelp>
          ) : (
            <FormHelp>
              Your offer won&#39;t post if it includes contact info - phone numbers, urls, or email addresses.
            </FormHelp>
          )
        }
        fieldName="description"
        flex="1"
        initialValue={initialDescription || ''}
        inputType="textarea"
        label="Description"
        maxLength="255"
        placeholder="Example: Get $500 off your first month's rent when you sign by October 15."
        onBlur={(e: ChangeEvent<HTMLInputElement>) => {
          actions.updateIsSaveDisabled({ reducerDispatch, isSaveDisabled: e.target.value === '' });
          clearServerError();
          setErrorObj({});
          setDescription(e.target.value);
          setSpecialOffer({ ...specialOffer, description: e.target.value });
        }}
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleChangeDebounced(e)}
      />
    </div>
  );
};

export default SpecialOfferFormField;
